import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon, AIDisclaimer } from "@/components/kit";
import { toast } from "sonner";

const TEMPLATES = ["Educational", "Market update", "Product education", "Investor awareness", "Festival communication", "Financial planning", "SIP education", "Retirement", "Tax planning"];
const CHANNELS = [
  { key: "linkedin", label: "LinkedIn Post", icon: "Linkedin" },
  { key: "instagram", label: "Instagram Post", icon: "Instagram" },
  { key: "reel_script", label: "Reel Script", icon: "Video" },
  { key: "youtube_script", label: "YouTube Script", icon: "Youtube" },
  { key: "whatsapp", label: "WhatsApp Message", icon: "MessageCircle" },
  { key: "email", label: "Email", icon: "Mail" },
  { key: "blog", label: "Blog", icon: "FileText" },
];

export default function ContentStudio() {
  const { refreshUser } = useAuth();
  const [topic, setTopic] = useState("Why investors should continue SIPs during market volatility");
  const [template, setTemplate] = useState("Educational");
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [imgs, setImgs] = useState([]);
  const [frames, setFrames] = useState([]);
  const [imgBusy, setImgBusy] = useState(false);
  const [sbBusy, setSbBusy] = useState(false);
  const [tab, setTab] = useState("content");
  const [imgPrompt, setImgPrompt] = useState("A festive Diwali greeting poster for mutual fund clients with diyas and rupee coins");
  const [imgModel, setImgModel] = useState("gemini");
  const [refImg, setRefImg] = useState(null);
  const [genImg, setGenImg] = useState(null);
  const [singleBusy, setSingleBusy] = useState(false);
  const refInput = useRef(null);
  const onRef = (e) => { const f = e.target.files?.[0]; if (!f) return; const r = new FileReader(); r.onload = () => setRefImg(r.result); r.readAsDataURL(f); };
  const genImage = async () => {
    if (!imgPrompt.trim()) return toast.error("Enter a prompt");
    setSingleBusy(true); setGenImg(null);
    try {
      const { data } = await api.post("/content/image", { prompt: imgPrompt, model: imgModel, reference: refImg });
      setGenImg(data.data_url); refreshUser();
      toast.success(data.edited ? "Image edited" : "Image generated");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Insufficient wallet balance. Please top up.");
      else toast.error("Image generation failed. Try again.");
    } finally { setSingleBusy(false); }
  };
  const location = useLocation();
  const seeded = useRef(false);
  useEffect(() => {
    if (!seeded.current && location.state?.topic) {
      seeded.current = true;
      const t = location.state.topic;
      setTopic(t);
      window.history.replaceState({}, "");
      generate(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const genVisual = async (path, setter, setB) => {
    setB(true);
    try {
      const { data } = await api.post(path, { topic });
      setter(data.images || data.frames || []); refreshUser();
      toast.success("Visuals ready");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Insufficient wallet balance. Please top up.");
      else toast.error("Image generation failed. Try again.");
    } finally { setB(false); }
  };

  const generate = async (overrideTopic) => {
    const t = typeof overrideTopic === "string" ? overrideTopic : topic;
    if (!t.trim()) return toast.error("Enter a topic");
    setBusy(true); setContent(null); setImgs([]); setFrames([]);
    try {
      const { data } = await api.post("/content/studio", { topic: t, template });
      setContent(data.content); refreshUser();
      toast.success("Content pack ready");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Insufficient wallet balance. Please top up.");
      else toast.error("Generation failed. Try again.");
    } finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Copied"); };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Content Studio" subtitle="One topic → publish-ready content for every channel, in natural Indian advisory language." icon="PenSquare" color="#8B5CF6" />

      <div className="flex gap-2 mb-5" data-testid="cs-tabs">
        {[["content", "Content Pack", "PenSquare"], ["image", "Image Studio", "Image"]].map(([k, l, ic]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cs-tab-${k}`} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border ${tab === k ? "grad-blue-violet text-white border-transparent" : "border-gray-200 text-gray-600 bg-white"}`}><Icon name={ic} size={15} /> {l}</button>
        ))}
      </div>

      {tab === "content" && (<>
      <Card className="mb-6">
        <label className="text-sm font-medium">Topic</label>
        <textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="content-topic-input"
          className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400" placeholder="e.g. Why SIPs beat timing the market" />
        <label className="text-sm font-medium block mt-4">Template</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {TEMPLATES.map((t) => (
            <button key={t} onClick={() => setTemplate(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${template === t ? "grad-blue-violet text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{t}</button>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Btn onClick={generate} disabled={busy} data-testid="content-generate-btn"><Icon name="Sparkles" size={16} /> {busy ? "Generating…" : "Generate content pack"}</Btn>
          <span className="text-sm text-gray-400">Cost: <b className="text-gray-700">₹10</b></span>
        </div>
      </Card>

      {busy && <div className="text-center text-gray-400 py-10"><Icon name="Loader" size={28} className="mx-auto mb-2 animate-spin" />Writing your multi-channel content…</div>}

      {content && (
        <>
          <div className="grid md:grid-cols-2 gap-4" data-testid="content-output">
            {CHANNELS.filter((c) => content[c.key]).map((c) => (
              <Card key={c.key}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-violet-50 grid place-items-center"><Icon name={c.icon} size={18} className="text-violet-600" /></div>
                  <h3 className="font-head font-semibold">{c.label}</h3>
                  <Btn variant="soft" className="ml-auto !px-3 !py-1.5" onClick={() => copy(content[c.key])} data-testid={`content-copy-${c.key}`}><Icon name="Copy" size={13} /> Copy</Btn>
                </div>
                <div className="text-sm whitespace-pre-wrap text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4 max-h-72 overflow-y-auto">{content[c.key]}</div>
              </Card>
            ))}
          </div>

          <Card className="mt-4">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-orange-50 grid place-items-center"><Icon name="Image" size={18} className="text-orange-600" /></div>
              <div><h3 className="font-head font-semibold">Visuals</h3><p className="text-xs text-gray-400">AI-generated, ready to post</p></div>
              <div className="ml-auto flex flex-wrap gap-2">
                <Btn variant="outline" onClick={() => genVisual("/content/visuals", setImgs, setImgBusy)} disabled={imgBusy} data-testid="gen-images-btn"><Icon name="Sparkles" size={14} /> {imgBusy ? "Creating…" : "Generate images (₹10)"}</Btn>
                <Btn variant="outline" onClick={() => genVisual("/content/storyboard", setFrames, setSbBusy)} disabled={sbBusy} data-testid="gen-storyboard-btn"><Icon name="Video" size={14} /> {sbBusy ? "Creating…" : "Reel storyboard (₹12)"}</Btn>
              </div>
            </div>
            {(imgBusy || sbBusy) && <div className="text-center text-gray-400 py-6"><Icon name="Loader" size={24} className="mx-auto mb-2 animate-spin" />Generating visuals… this can take ~15–30s</div>}
            {imgs.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 mb-4" data-testid="images-output">
                {imgs.map((im) => (
                  <div key={im.kind} className="rounded-xl overflow-hidden border border-gray-100">
                    <img src={im.data_url} alt={im.label} className="w-full object-cover" />
                    <div className="flex items-center gap-2 p-2 bg-gray-50 text-xs">
                      <span className="font-medium">{im.label} · {im.aspect}</span>
                      <a href={im.data_url} download={`mfdx-${im.kind}.png`} className="ml-auto text-blue-600 font-semibold flex items-center gap-1" data-testid={`img-download-${im.kind}`}><Icon name="Download" size={13} /> Download</a>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {frames.length > 0 && (
              <div>
                <h4 className="font-head font-semibold text-sm mb-2">Reel storyboard</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="storyboard-output">
                  {frames.map((f, i) => (
                    <div key={f.label} className="rounded-xl overflow-hidden border border-gray-100">
                      <img src={f.data_url} alt={f.label} className="w-full object-cover aspect-[9/16]" />
                      <div className="flex items-center gap-2 p-2 bg-gray-50 text-xs">
                        <span className="font-medium">{i + 1}. {f.label}</span>
                        <a href={f.data_url} download={`mfdx-frame-${i + 1}.png`} className="ml-auto text-blue-600 font-semibold" data-testid={`frame-download-${i}`}><Icon name="Download" size={13} /></a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
          <AIDisclaimer />
        </>
      )}
      </>)}

      {tab === "image" && (
        <Card className="mb-6" data-testid="image-studio">
          <label className="text-sm font-medium">Describe the image you want</label>
          <textarea rows={3} value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)} data-testid="image-prompt-input"
            className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400" placeholder="e.g. A Diwali greeting poster with diyas, rupee coins and a warm festive message" />

          <label className="text-sm font-medium block mt-4">Model</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {[["gemini", "Gemini Nano Banana"], ["openai", "OpenAI GPT Image 1"]].map(([k, l]) => (
              <button key={k} onClick={() => setImgModel(k)} data-testid={`image-model-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${imgModel === k ? "grad-blue-violet text-white border-transparent" : "border-gray-200 text-gray-600"}`}>{l}</button>
            ))}
          </div>

          <label className="text-sm font-medium block mt-4">Reference image <span className="text-gray-400 font-normal">(optional — enables editing)</span></label>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <label className="cursor-pointer text-xs font-semibold rounded-lg px-3 py-2 bg-gray-100 text-gray-700 inline-flex items-center gap-1.5">
              <Icon name="Upload" size={14} /> Upload reference
              <input ref={refInput} type="file" accept="image/*" onChange={onRef} data-testid="image-ref-input" className="hidden" />
            </label>
            {refImg && (<div className="flex items-center gap-2"><img src={refImg} alt="reference" className="w-12 h-12 rounded-lg object-cover border border-gray-200" /><button onClick={() => { setRefImg(null); if (refInput.current) refInput.current.value = ""; }} data-testid="image-ref-clear" className="text-xs text-red-500 font-semibold">Remove</button></div>)}
          </div>
          {refImg && <p className="text-[11px] text-gray-400 mt-1">Editing uses Gemini Nano Banana to modify your reference image.</p>}

          <div className="flex items-center gap-3 mt-5">
            <Btn onClick={genImage} disabled={singleBusy} data-testid="image-generate-btn"><Icon name={singleBusy ? "Loader" : "Sparkles"} size={16} className={singleBusy ? "animate-spin" : ""} /> {singleBusy ? (refImg ? "Editing…" : "Generating…") : (refImg ? "Edit image" : "Generate image")}</Btn>
            <span className="text-sm text-gray-400">Cost: <b className="text-gray-700">₹5</b></span>
          </div>

          {singleBusy && <div className="text-center text-gray-400 py-8"><Icon name="Loader" size={26} className="mx-auto mb-2 animate-spin" />Creating your image… this can take up to a minute</div>}
          {genImg && (
            <div className="mt-5" data-testid="image-result">
              <div className="rounded-xl overflow-hidden border border-gray-100 inline-block max-w-full">
                <img src={genImg} alt="Generated" className="max-w-full max-h-[420px] object-contain bg-gray-50" />
              </div>
              <div className="flex gap-4 mt-3">
                <a href={genImg} download="mfdx-image.png" className="text-sm font-semibold text-blue-600 inline-flex items-center gap-1" data-testid="image-download"><Icon name="Download" size={14} /> Download</a>
                <button onClick={() => { setRefImg(genImg); toast.success("Set as reference — describe your edit"); }} data-testid="image-use-ref" className="text-sm font-semibold text-violet-600 inline-flex items-center gap-1"><Icon name="Wand2" size={14} /> Edit this image</button>
              </div>
            </div>
          )}
          <AIDisclaimer />
        </Card>
      )}
    </div>
  );
}
